import {useState} from 'react';
import NextLink from 'next/link';

import {
  CreateCheckInAnswersInputAnswer,
  useCheckInQuery,
  useCreateCheckInAnswers,
} from '#apollo/schema';
import {Button, Card, Icon, Screen, Text} from '#component/base';
import {ModalRoot} from '#component/base/Modal';
import {CheckInExitModal} from '#component/modal';
import {HealthFactorExpandedCard} from '#component/partial';
import {CarouselProvider} from '#component/provider';
import {Transition} from '#component/util';
import {withRequireUserState} from '#component/util/RequireUserState/RequireUserState';
import {ImpactLevel} from '#constant/impactLevel';
import {sprinkles} from '#style/sprinkles';

import {
  getAchievedActionableQuestions,
  getFormattedCheckInDateRange,
  getFormattedEarliestCheckInDate,
  getUnachievedActionableQuestions,
} from './CheckIn.util';
import {
  CelebrationCard,
  CheckInModal,
  CheckInStats,
  EmptyPage,
  FooterCTA,
  Header,
  QuestionCarousel,
} from './component';

import * as css from './CheckIn.css';

const defaultScreenProps = {
  className: sprinkles({bg: 'backgroundGradientBurgundy'}),
  contentContainerClassName: css.screenContainer,
  hideHeader: true,
  pageName: 'Check In',
  withDefaultSizing: false,
};

export const CheckInScreen = () => {
  const [open, setOpen] = useState(false);
  const [exitModalOpen, setExitModalOpen] = useState(false);
  const [createCheckInAnswers, {loading: submissionLoading}] = useCreateCheckInAnswers({
    onCompleted: () => setOpen(false),
  });
  const {data, loading} = useCheckInQuery({
    onCompleted: ({currentCheckIn}) => {
      if (!!currentCheckIn && !currentCheckIn.completed) {
        setOpen(true);
      }
    },
  });

  const {currentCheckIn, currentTestPeriod, me} = data || {};
  const questions = currentCheckIn?.questions || [];
  const achievedQuestions = getAchievedActionableQuestions(questions);
  const unachievedQuestions = getUnachievedActionableQuestions(questions);
  const expandedQuestion =
    achievedQuestions.length < 1
      ? unachievedQuestions[Math.floor(Math.random() * unachievedQuestions.length)]
      : null;
  const hasActiveSubscription = !!me?.subscription?.hasActiveSubscription;
  const hasActionPlan =
    !!me?.mostRecentLabSubmissionWithResults?.userSurveySubmission?.surveyAssessment
      ?.selectedActionPlan;

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setExitModalOpen(true);
    }
  };

  const handleSubmit = (answers: CreateCheckInAnswersInputAnswer[]) => {
    if (currentCheckIn) {
      createCheckInAnswers({variables: {input: {answers, checkInId: currentCheckIn.id}}});
    }
  };

  if (!hasActiveSubscription) {
    return (
      <Screen {...defaultScreenProps} loading={loading}>
        <EmptyPage
          ctaProps={{children: 'Subscribe', href: '/profile?tab=manage'}}
          body={
            <>
              <Text as="p" color="cotton" fontWeight="semiBold">
                Your membership is inactive.
              </Text>

              <Text as="p" color="cotton">
                Subscribe to access additional features such as Action Plans and Check-ins
              </Text>
            </>
          }
        />
      </Screen>
    );
  }

  if (!hasActionPlan || !currentTestPeriod) {
    return (
      <Screen {...defaultScreenProps} loading={loading}>
        <EmptyPage
          ctaProps={{children: 'Select an Action Plan', href: '/actionplan'}}
          body="Your Action Plan is inactive which means check-ins are unavailable. Please select an Action Plan to opt into this feature."
        />
      </Screen>
    );
  }

  if (!currentCheckIn) {
    return (
      <Screen {...defaultScreenProps} loading={loading}>
        <EmptyPage
          body={
            <div className={sprinkles({dir: 'col', flex: 'flex', gap: 16})}>
              <Text as="p" color="cotton">
                We’re happy to see you’re eager to start your check-in cycle! Our check-ins are on a
                weekly basis, and always start on Mondays.
              </Text>

              <Text as="p" color="cotton">
                {'Your check-in will be available on: '}

                <Text color="inherit" fontWeight="semiBold">
                  {getFormattedEarliestCheckInDate(currentTestPeriod?.earliestCheckInStartOn)}
                </Text>
              </Text>
            </div>
          }
          ctaProps={{children: 'View my Action Plan', href: '/actionplan'}}
          headline="Stay tuned!"
          iconName="CheckCircle"
        />
      </Screen>
    );
  }

  return (
    <Screen {...defaultScreenProps} loading={loading || submissionLoading}>
      {!open && (
        <>
          <header
            className={sprinkles({
              content: 'between',
              flex: 'flex',
              items: 'center',
              pT: [24, 32],
            })}>
            <Text
              as="h1"
              className={sprinkles({grow: 1, w: 'full'})}
              color="white"
              textAlign="center"
              variant="label">
              Check-in complete
            </Text>

            <NextLink href="/dashboard" passHref>
              <a className={css.button}>
                <Icon aria-label="Go to Dashboard" color="white" name="X" />
              </a>
            </NextLink>
          </header>

          <Card appear show as={Transition} className={css.card} variant="white">
            <div
              className={sprinkles({
                flex: 'flex',
                dir: 'col',
                gap: 32,
                pT: [24, 48],
                pX: [16, 64],
              })}>
              <Text
                as="p"
                className={sprinkles({bg: 'springWood', radius: 4, pX: 8, pY: 4, w: 'maxContent'})}
                color="deepBurgundy"
                variant="caption">
                {getFormattedCheckInDateRange(currentCheckIn?.startedOn, currentCheckIn?.endedOn)}
              </Text>

              <div
                className={sprinkles({
                  dir: ['col', 'col', 'row'],
                  flex: 'flex',
                  gap: 32,
                  items: ['stretch', 'stretch', 'center'],
                })}>
                <Header percentage={currentCheckIn?.actionItemsHitPercentage} />

                <CelebrationCard
                  className={css.celebrationCard}
                  isFirstCheckIn={currentTestPeriod.completedCheckIns === 1}
                  percentage={currentCheckIn?.actionItemsHitPercentage}
                  streakCount={currentTestPeriod?.checkInStreakCount}
                />
              </div>

              <hr className={css.divider} />
            </div>

            {!!expandedQuestion ? (
              <div className={sprinkles({flex: 'flex', dir: 'col', gap: 16, pX: [16, 64]})}>
                <Text color="deepBurgundy" variant="heading3">
                  Start doing
                </Text>

                <HealthFactorExpandedCard
                  className={sprinkles({mX: 'auto'})}
                  headline={expandedQuestion.actionItemTitle}
                  imageUrl={expandedQuestion.imageUrl}
                  impact={expandedQuestion.impactLevel as ImpactLevel}
                  tips={expandedQuestion.tips}
                />
              </div>
            ) : (
              <div className={sprinkles({flex: 'flex', dir: 'col', gap: 40})}>
                {unachievedQuestions.length > 0 && (
                  <CarouselProvider>
                    <QuestionCarousel headline="Start doing" questions={unachievedQuestions} />
                  </CarouselProvider>
                )}

                <CarouselProvider>
                  <QuestionCarousel headline="Keep doing" questions={achievedQuestions} />
                </CarouselProvider>
              </div>
            )}

            <div className={sprinkles({flex: 'flex', dir: 'col', gap: 8, pT: 24, pX: [16, 64]})}>
              <NextLink href="/lifestylesummary" passHref>
                <Button as="a" variant="secondary">
                  View Lifestyle Summary
                </Button>
              </NextLink>

              <NextLink href="/actionplan" passHref>
                <Button as="a" variant="secondary">
                  View Action Plan
                </Button>
              </NextLink>
            </div>

            <footer className={css.footer}>
              <CheckInStats
                className={sprinkles({pX: [0, 64]})}
                checkIns={currentTestPeriod?.checkInStats}
              />

              <div
                className={sprinkles({
                  dir: 'col',
                  flex: 'flex',
                  gap: 32,
                  pB: [24, 48],
                  pX: [16, 64],
                })}>
                <hr className={css.divider} />

                <FooterCTA
                  hasActionPlan={hasActionPlan}
                  hasActiveSubscription={hasActiveSubscription}
                  nextCheckInDate={currentCheckIn?.endedOn}
                />
              </div>
            </footer>
          </Card>
        </>
      )}

      <ModalRoot onOpenChange={handleOpenChange} open={open}>
        <CheckInModal
          endedOn={currentCheckIn?.endedOn}
          onSubmit={handleSubmit}
          questions={questions}
          startedOn={currentCheckIn?.startedOn}
        />
      </ModalRoot>

      <ModalRoot onOpenChange={setExitModalOpen} open={exitModalOpen}>
        <CheckInExitModal />
      </ModalRoot>
    </Screen>
  );
};

export const CheckIn = withRequireUserState(CheckInScreen, {userState: 'verified'});
